import type { NonSubscribableProduct } from '$models/NonSubscribableProduct/NonSubscribableProduct';
import type { OrderItem } from '$models/Order/Order';
import type { OrderConfigurationOrder } from '$models/OrderConfigurator/OrderConfigurator';
import type { Product } from '$models/Product/Product';
import type { ProductCategoryWithQuantities } from '$models/Product/ProductCategory';
import type { OrderFrequency } from '$models/Webparking/OrderFrequencies';
import type { OrderDraft } from './order-draft.svelte';
import { onNavigate } from '$app/navigation';
import { getContext, setContext, untrack } from 'svelte';
import { createConfiguratorState } from './configurator.svelte';
import { getOrderDraftState } from './order-draft.svelte';

const key = Symbol('cartState');

type CartState = ReturnType<typeof createCartState>;
type AddOrderResult = {
	mixedCartBlocked: boolean;
};

type CreateCartArguments = {
	allProducts: {
		regular: Product[];
		nonSubscribable: NonSubscribableProduct[];
	};
	allFrequencies: OrderFrequency[];
	categoriesWithQuantities: ProductCategoryWithQuantities[];
};

type CartConfiguratorConfiguration = ReturnType<typeof createConfiguratorConfiguration>;

function normalizeCouponCode(value: string | null) {
	const normalizedValue = value?.trim() ?? '';
	return normalizedValue.length > 0 ? normalizedValue : null;
}

function createCartContentSignature(
	cart: Pick<OrderDraft, 'frequency' | 'products' | 'nonSubscribableProducts' | 'couponCode'> | null
) {
	if (!cart) {
		return 'null';
	}

	return JSON.stringify({
		frequency: cart.frequency,
		products: cart.products,
		nonSubscribableProducts: cart.nonSubscribableProducts,
		couponCode: normalizeCouponCode(cart.couponCode)
	});
}

function createConfiguratorConfiguration(
	cart: Pick<OrderDraft, 'frequency' | 'products' | 'nonSubscribableProducts'> | null,
	allProducts: CreateCartArguments['allProducts'],
	allFrequencies: OrderFrequency[],
	categoriesWithQuantities: ProductCategoryWithQuantities[]
) {
	if (!cart) {
		return;
	}

	const formattedProducts = cart.products
		.map((cartProduct) => {
			const fullProduct = allProducts.regular.find((p) => p.id === cartProduct.id);
			if (!fullProduct) {
				return null;
			}

			return {
				product: fullProduct,
				quantity: cartProduct.quantity
			};
		})
		.filter((cartProduct) => cartProduct !== null)
		.filter((cartProduct) => cartProduct.quantity > 0);
	const cartCategory = formattedProducts[0]?.product.category;
	let formattedNonSubscribableProducts: {
		product: NonSubscribableProduct;
		quantity: number;
	}[] = [];

	if (cart.nonSubscribableProducts.length > 0) {
		formattedNonSubscribableProducts = cart.nonSubscribableProducts
			.map((cartProduct) => {
				const fullProduct = allProducts.nonSubscribable.find((p) => p.id === cartProduct.id);
				if (!fullProduct) {
					return null;
				}

				return {
					product: fullProduct,
					quantity: cartProduct.quantity
				};
			})
			.filter((cartProduct) => cartProduct !== null)
			.filter((cartProduct) => cartProduct.quantity > 0);
	}

	if (!formattedProducts.length && !formattedNonSubscribableProducts.length) {
		return;
	}

	return {
		categories: cartCategory
			? [
					{
						productCategory: cartCategory,
						quantities:
							categoriesWithQuantities
								.find((cq) => cq.category.id === cartCategory.id)
								?.quantities.map((q) => q.quantity) ?? [],
						defaultQuantity: formattedProducts.reduce((acc, p) => acc + p.quantity, 0),
						products: formattedProducts
					}
				]
			: [],
		nonSubscribableProducts: formattedNonSubscribableProducts,
		frequencies: allFrequencies.map((f) => f.weeks),
		defaultFrequency: cart.frequency,
		defaultCategory: cartCategory ?? null
	};
}

export function createCartState({
	allProducts,
	allFrequencies,
	categoriesWithQuantities
}: CreateCartArguments) {
	const orderDraft = getOrderDraftState();
	let couponCode = $derived(orderDraft.current?.couponCode ?? '');
	let isOpen = $state(false);
	let configuratorConfiguration: CartConfiguratorConfiguration = $state.raw(
		createConfiguratorConfiguration(
			orderDraft.current,
			allProducts,
			allFrequencies,
			categoriesWithQuantities
		)
	);
	let configurator: ReturnType<typeof createConfiguratorState> | undefined = $state.raw(
		createCartConfigurator(untrack(() => configuratorConfiguration))
	);
	let configuratorRevision = $state(0);
	let cleanupConfiguratorRoot = () => {};
	let draftCartContentSignature = $derived(createCartContentSignature(orderDraft.current));
	let appliedDraftCartContentSignature = createCartContentSignature(orderDraft.current);
	let products = $derived.by(() => {
		if (!configurator || !orderDraft.current) {
			return [];
		}

		const products = configurator.products();

		return products.filter((productItem) => {
			return orderDraft.current!.products.find(
				(cartProductItem) => cartProductItem.id === productItem.product.id
			);
		});
	});
	let nonSubscribableProducts = $derived.by(() => {
		if (!configurator || !orderDraft.current) {
			return [];
		}

		const nonSubscribableProducts = configurator.nonSubscribableProducts();

		return nonSubscribableProducts.filter((productItem) => {
			return orderDraft.current!.nonSubscribableProducts.find(
				(cartProductItem) => cartProductItem.id === productItem.product.id
			);
		});
	});
	let isEmpty = $derived(products.length === 0 && nonSubscribableProducts.length === 0);

	function getRestoredCouponCode() {
		return untrack(() => normalizeCouponCode(orderDraft.current?.couponCode ?? null));
	}

	function createCartConfigurator(configuration: CartConfiguratorConfiguration) {
		if (!configuration) {
			return;
		}

		const nextConfigurator = createConfiguratorState(configuration, {
			pricingActivated: false
		});

		// In the cart configurator the cart frequency is set to 2 by default.
		nextConfigurator.setRecurringFrequency(
			configuration.defaultFrequency > 0 ? configuration.defaultFrequency : 2
		);

		const restoredCouponCode = getRestoredCouponCode();
		nextConfigurator.setCouponCodes({
			recurringCode: restoredCouponCode || '',
			onetimeCode: restoredCouponCode || ''
		});

		return nextConfigurator;
	}

	function schedulePricingActivation(revision: number) {
		queueMicrotask(() => {
			if (configuratorRevision !== revision || !configurator || configurator.isPricingActivated()) {
				return;
			}

			void configurator.activatePricing();
		});
	}

	function rebuildConfigurator() {
		configuratorRevision += 1;
	}

	function setConfiguratorConfiguration(configuration: CartConfiguratorConfiguration) {
		configuratorConfiguration = configuration;
		rebuildConfigurator();
	}

	function createCurrentConfiguratorConfiguration() {
		return createConfiguratorConfiguration(
			orderDraft.current,
			allProducts,
			allFrequencies,
			categoriesWithQuantities
		);
	}

	function applyConfiguratorCouponCode(newValue: string | null) {
		const normalizedValue = normalizeCouponCode(newValue);
		configurator?.setCouponCodes({
			recurringCode: normalizedValue || '',
			onetimeCode: normalizedValue || ''
		});
	}

	function setCouponCode(newValue: string | null) {
		const normalizedValue = normalizeCouponCode(newValue);

		if (orderDraft.current) {
			orderDraft.updateDraft({ couponCode: normalizedValue });
			appliedDraftCartContentSignature = createCartContentSignature(orderDraft.current);
		}

		applyConfiguratorCouponCode(normalizedValue);
	}

	// When navigating anywhere, make sure the cart closes.
	onNavigate(() => close());

	function open() {
		isOpen = true;
	}

	function close() {
		isOpen = false;
	}

	function addOrIncrementProducts(currentProducts: OrderItem[], newProducts: OrderItem[]) {
		let mergedProducts: OrderItem[] = currentProducts;

		newProducts.forEach((item) => {
			const existingProduct = mergedProducts.find(({ id }) => id === item.id);

			if (!existingProduct) {
				mergedProducts = [...mergedProducts, item];
				return;
			}

			mergedProducts = mergedProducts.map((product) => {
				if (product.id === item.id) {
					return {
						id: item.id,
						quantity: product.quantity + item.quantity
					};
				}

				return product;
			});
		});

		return mergedProducts;
	}

	function getProductItem(id: string) {
		const product = products.find((p) => p.product.id === id);
		const nonSubscribableProduct = nonSubscribableProducts.find((p) => p.product.id === id);

		return product || nonSubscribableProduct;
	}

	function hasProduct(id: string) {
		const productItem = getProductItem(id);
		return !!productItem;
	}

	function addProducts(items: OrderItem[]) {
		const newProducts = addOrIncrementProducts(
			products.map((p) => ({ id: p.product.id, quantity: p.quantity })),
			items
		);
		setProducts(newProducts);
	}

	function setProducts(newProducts: OrderItem[]) {
		orderDraft.updateDraft((draft) => ({
			products: newProducts,
			frequency: draft?.frequency ?? 0,
			nonSubscribableProducts: draft?.nonSubscribableProducts ?? [],
			couponCode: normalizeCouponCode(draft?.couponCode ?? couponCode)
		}));
		setConfiguratorConfiguration(createCurrentConfiguratorConfiguration());
		appliedDraftCartContentSignature = createCartContentSignature(orderDraft.current);
	}

	function getOrderCategory(order: OrderConfigurationOrder) {
		const firstProduct = order.products[0];
		if (!firstProduct) {
			return;
		}

		return allProducts.regular.find((product) => product.id === firstProduct.id)?.category;
	}

	function addOrder(order: OrderConfigurationOrder): AddOrderResult {
		const cartCategory = configurator?.selectedCategory();
		const orderCategory = getOrderCategory(order);
		const shouldReplaceProducts =
			order.products.length > 0 && cartCategory?.id !== orderCategory?.id;
		const mixedCartBlocked = shouldReplaceProducts && !isEmpty;
		const currentDraft = orderDraft.current;
		const nextCart = {
			...(currentDraft ?? {}),
			products: shouldReplaceProducts
				? order.products
				: addOrIncrementProducts(currentDraft?.products ?? [], order.products),
			frequency: order.frequency,
			nonSubscribableProducts: addOrIncrementProducts(
				currentDraft?.nonSubscribableProducts ?? [],
				order.nonSubscribableProducts
			),
			couponCode: normalizeCouponCode(order.couponCode ?? currentDraft?.couponCode ?? couponCode)
		};

		orderDraft.updateDraft(nextCart);
		setConfiguratorConfiguration(
			createConfiguratorConfiguration(
				nextCart,
				allProducts,
				allFrequencies,
				categoriesWithQuantities
			)
		);
		appliedDraftCartContentSignature = createCartContentSignature(nextCart);

		return { mixedCartBlocked };
	}

	function setCart(order: OrderConfigurationOrder) {
		orderDraft.updateDraft({
			...order,
			couponCode: normalizeCouponCode(order.couponCode)
		});
		setConfiguratorConfiguration(createCurrentConfiguratorConfiguration());
		appliedDraftCartContentSignature = createCartContentSignature(orderDraft.current);
	}

	function addNonSubscribableProducts(items: OrderItem[]) {
		if (!orderDraft.current) {
			return;
		}

		orderDraft.updateDraft((draft) => ({
			nonSubscribableProducts: addOrIncrementProducts(draft?.nonSubscribableProducts ?? [], items)
		}));
		setConfiguratorConfiguration(createCurrentConfiguratorConfiguration());
		appliedDraftCartContentSignature = createCartContentSignature(orderDraft.current);
	}

	function syncFromConfigurator() {
		if (!configurator) {
			return;
		}

		const isValid = configurator.isValid();
		const order = configurator.order();

		untrack(() => {
			const currentDraft = orderDraft.current;
			if (!currentDraft) {
				return;
			}

			const nextCart =
				isValid || order.products.length === 0
					? {
							...order,
							couponCode: normalizeCouponCode(order.couponCode)
						}
					: {
							products: currentDraft.products,
							nonSubscribableProducts: order.nonSubscribableProducts,
							couponCode: normalizeCouponCode(order.couponCode),
							frequency: order.frequency
						};

			const nextCartSignature = createCartContentSignature(nextCart);
			if (nextCartSignature === createCartContentSignature(currentDraft)) {
				appliedDraftCartContentSignature = nextCartSignature;
				return;
			}

			orderDraft.updateDraft(nextCart);
			appliedDraftCartContentSignature = nextCartSignature;
		});
	}

	// Rebuild the configurator whenever the persisted draft changes, including cross-tab changes.
	$effect(() => {
		if (draftCartContentSignature !== appliedDraftCartContentSignature) {
			setConfiguratorConfiguration(createCurrentConfiguratorConfiguration());
			appliedDraftCartContentSignature = draftCartContentSignature;
		}
	});

	// Recreate the cart configurator when the stored cart meaningfully changes.
	$effect(() => {
		const revision = configuratorRevision;
		const configuration = configuratorConfiguration;

		cleanupConfiguratorRoot();
		cleanupConfiguratorRoot = () => {};

		if (!configuration) {
			configurator = undefined;
			return;
		}

		cleanupConfiguratorRoot = $effect.root(() => {
			const nextConfigurator = createCartConfigurator(configuration);
			configurator = nextConfigurator;

			if (nextConfigurator) {
				schedulePricingActivation(revision);
			}

			return () => {
				configurator = undefined;
			};
		});

		return () => {
			cleanupConfiguratorRoot();
			cleanupConfiguratorRoot = () => {};
		};
	});

	// Close the cart when it ends up in an empty state
	$effect(() => {
		if (isEmpty && isOpen) {
			close();
		}
	});

	untrack(() => {
		if (configurator) {
			schedulePricingActivation(configuratorRevision);
		}
	});

	return {
		isOpen() {
			return isOpen;
		},
		open,
		close,
		hasProduct,
		getProductItem,
		addProducts,
		setProducts,
		addOrder,
		addNonSubscribableProducts,
		syncFromConfigurator,
		setCart,
		toggle() {
			isOpen ? close() : open();
		},
		get configurator() {
			return configurator;
		},
		isEmpty() {
			return isEmpty;
		},
		clear() {
			orderDraft.clear();
			setConfiguratorConfiguration(createCurrentConfiguratorConfiguration());
			appliedDraftCartContentSignature = createCartContentSignature(orderDraft.current);
		},
		anonymize() {
			orderDraft.anonymize();
			setConfiguratorConfiguration(createCurrentConfiguratorConfiguration());
			appliedDraftCartContentSignature = createCartContentSignature(orderDraft.current);
		},
		get products() {
			return products;
		},
		get nonSubscribableProducts() {
			return nonSubscribableProducts;
		},
		get couponCode() {
			return couponCode;
		},
		isValidCoupon() {
			const discountData = configurator?.discountData();

			if (!discountData) {
				return false;
			}

			return false;
		},
		setCouponCode
	};
}

export function setCartState(cartState: CartState) {
	setContext(key, cartState);
}

export function getCartState(): CartState {
	return getContext(key);
}
